/**
 * F1 Oracle — keyword → mock response mapping.
 * -------------------------------------------------------------------------
 * The Oracle is the production app's primary AI surface. Until the live LLM
 * backend is wired up (see `oracleService.js`), this file backs the UX with
 * a curated set of long-form responses so the chat interface can be
 * developed against realistic message shapes.
 *
 * Response shape:
 *   id        unique key (debug + analytics)
 *   keywords  case-insensitive substrings that route to this response
 *   tone      'analytical' | 'historical' | 'conversational' — influences
 *             the avatar styling on the bubble
 *   blocks    ordered array of content blocks. Each block is one of:
 *               { type: 'paragraph', text: string }
 *               { type: 'heading', text: string }
 *               { type: 'list', items: string[] }
 *               { type: 'data', label: string, rows: Array<{k,v}> }
 *               { type: 'quote', text: string, attribution: string }
 *               { type: 'callout', text: string }
 *
 * Production note for the broader team: when the real LLM lands, the
 * backend should return blocks in the same shape so the renderer in
 * `ChatBubble.jsx` does not have to change. The streaming variant will
 * additionally emit `{ type: 'partial', text: string }` deltas — see the
 * TODO in `oracleService.js`.
 */

// NOTE: the telemetry entry that used to sit here ('resp-telemetry-norris-miami')
// invented a 2026 Miami GP lap for Norris — specific fake sector splits and top
// speeds — and a telemetry question with no live OpenF1 data fell through to it,
// stating fiction as fact. Same failure class as the Miami race-result bug (#21).
// Deleted: real telemetry is served live from OpenF1 (oracleService), and a miss
// now degrades to the knowledge base or an honest fallback, never an invented lap.
export const oracleResponses = [
  {
    id: 'resp-history-senna',
    keywords: ['senna', 'ayrton', 'imola 1994', 'history', 'legend', 'greatest', 'g.o.a.t', 'goat'],
    tone: 'historical',
    blocks: [
      {
        type: 'paragraph',
        text: "Ayrton Senna won three world championships (1988, 1990, 1991) and is widely considered the most naturally gifted qualifier the sport has produced. His 65 career pole positions stood as the record for twelve years after his death until Michael Schumacher passed it in 2006.",
      },
      {
        type: 'heading',
        text: 'Career highlights',
      },
      {
        type: 'list',
        items: [
          '41 race wins from 161 starts (25.5% win rate)',
          '65 pole positions — the wet-weather Donington 1993 pole is widely cited as his finest single lap',
          'Three-time World Champion (1988, 1990, 1991) — all with McLaren-Honda',
          '80 podium finishes',
          'Posthumously inducted into the FIA Hall of Fame in the inaugural 2017 class',
        ],
      },
      {
        type: 'quote',
        text: 'If you no longer go for a gap that exists, you are no longer a racing driver.',
        attribution: 'Ayrton Senna, 1990',
      },
      {
        type: 'paragraph',
        text: "Senna's safety legacy is arguably more important than his statistical record. His fatal accident at Imola in 1994 catalysed the GPDA's reformation and triggered the FIA's most aggressive safety review in the modern era — the foundation of every halo, headrest, and survival cell standard you see on the 2026 grid.",
      },
    ],
  },
  {
    id: 'resp-regulations-2026',
    keywords: ['regulation', '2026 car', 'new car', 'rules', 'hybrid', 'engine', 'power unit', 'active aero', 'manual override', 'drs replacement'],
    tone: 'analytical',
    blocks: [
      {
        type: 'paragraph',
        text: 'The 2026 regulations are the largest single-step technical reset in modern F1, hitting chassis, power unit, fuel, and on-track tooling simultaneously. The headline numbers:',
      },
      {
        type: 'heading',
        text: 'Power unit',
      },
      {
        type: 'data',
        label: 'Architecture · 1.6L V6 turbo-hybrid · 100% sustainable fuel',
        rows: [
          { k: 'ICE peak power', v: '~400 kW (down from 560 kW)' },
          { k: 'Electrical power (MGU-K)', v: '~350 kW (up from 120 kW)' },
          { k: 'Power split (race trim)', v: '≈ 50 / 50 ICE / electric' },
          { k: 'MGU-H', v: 'Removed' },
          { k: 'Fuel flow', v: 'Energy-limited, not mass-limited' },
        ],
      },
      {
        type: 'heading',
        text: 'Chassis & aero',
      },
      {
        type: 'list',
        items: [
          'Active aerodynamics replace DRS — drivers toggle between an "X-mode" low-drag setup on straights and a "Z-mode" high-downforce setup in corners',
          'Manual override: a strategic battery-deployment boost any driver can trigger when within 1.0s of the car ahead — the post-2026 overtaking mechanic',
          '~30 kg lighter minimum weight (chassis only — net weight reduction is ~10 kg once larger batteries are included)',
          'Smaller dimensions: 200 mm narrower, 100 mm shorter wheelbase target',
          'Two-stage front wing with FIA-controlled active flaps',
        ],
      },
      {
        type: 'paragraph',
        text: 'Sporting-side, the FIA also introduced energy-management strategy as a public element — TV graphics now display each car\'s remaining override deploys per lap, exposing what was previously a hidden layer of the strategy game.',
      },
      {
        type: 'callout',
        text: 'Cadillac and Audi are the two new power unit manufacturers brought in by these regulations. Ford returns in 2026 as Red Bull Powertrains\' technical partner.',
      },
    ],
  },
  {
    id: 'resp-strategy-undercut',
    keywords: ['undercut', 'overcut', 'pit strategy', 'tyre strategy', 'one-stop', 'two-stop'],
    tone: 'analytical',
    blocks: [
      {
        type: 'paragraph',
        text: "At a typical 2026-era circuit, the undercut delta — the lap-time benefit of pitting first to fresh rubber while the leader stays out on aged tyres — sits in the 0.7 to 1.1 second band per lap for the first three to four laps after the stop.",
      },
      {
        type: 'heading',
        text: 'When undercut wins',
      },
      {
        type: 'list',
        items: [
          'Track position is the dominant currency (Monaco, Hungary, Singapore)',
          'Pit lane loss is short enough that the fresh-tyre delta outpays the time lost',
          'Tyre degradation is high — fresh rubber gives meaningful pace, not just one-lap heroics',
          'Traffic is thin enough that the out-lap can be banked cleanly',
        ],
      },
      {
        type: 'heading',
        text: 'When overcut wins',
      },
      {
        type: 'list',
        items: [
          'Track temperature is climbing and harder compounds come into their window late',
          'The driver behind has already pitted, locking themselves into a strategy',
          'Tyre warm-up is slow on the new set (cold mornings, low track temps)',
          'Traffic on the out-lap is heavy — clean air is worth more than fresh rubber',
        ],
      },
      {
        type: 'paragraph',
        text: 'In 2026, the manual override system has added a third axis: a driver close behind on similar tyres can use a saved deploy cluster to defend against the undercut for one or two laps, effectively buying the strategist time. Watch for it on a chess-piece circuit like Imola next weekend.',
      },
    ],
  },
];

/**
 * Default fallback when no keyword route matches. Kept generic so the UI can
 * render confidently even when the user types something unexpected.
 */
export const defaultOracleResponse = {
  id: 'resp-fallback',
  tone: 'conversational',
  blocks: [
    {
      type: 'paragraph',
      text: "I don't have a curated answer for that yet — the production LLM backend will handle open-ended queries once it's connected. Race results and championship standings are answered from live data, so those always work.",
    },
    {
      type: 'heading',
      text: 'Suggested topics',
    },
    {
      type: 'list',
      items: [
        '"Who won the last race?"',
        '"Current drivers\' standings"',
        '"Explain the 2026 active aero rules"',
        '"Tell me about Senna"',
        '"When does the undercut work?"',
      ],
    },
  ],
};

// The first four resolve against live data via oracleFacts.js (standings, race
// result, schedule, qualifying). They used to include a hand-written 2026 Miami
// weekend that never happened; every prompt here now maps to something real.
export const suggestedPrompts = [
  { id: 'sp-standings', label: 'Current standings', icon: '📊' },
  { id: 'sp-last-race', label: 'Who won the last race?', icon: '🏁' },
  { id: 'sp-next-race', label: 'When is the next race?', icon: '📅' },
  { id: 'sp-pole', label: 'Who took pole last time?', icon: '⏱️' },
  { id: 'sp-regs', label: 'Explain 2026 regulations', icon: '⚙️' },
  { id: 'sp-history', label: 'Tell me about Senna', icon: '🏆' },
];
